import { useConfig } from "../../../contexts/ConfigContext"
import React, { useEffect, useState } from "react"
import { useCompany } from "../../../contexts/CompanyContext"
import { useContact } from "../../../contexts/ContactContext"
import { useContactsFetch } from "../../../hooks/useInventory"
import { useSecureRequest } from "../../../hooks/useSecureRequest"
import useAuth from "../../../hooks/useAuth"
import { UnifiedContactView } from "@ryo/ui"
import { UserOutlined, TeamOutlined } from "@ant-design/icons"
import { mutate } from "swr"

function ViewUserContactList(){
  const { config } = useConfig()
  const { contact: contactState, updateContact } = useContact()
  const { company } = useCompany()
  const { securePost } = useSecureRequest()
  const [activeRole, setActiveRole] = useState<'CUSTOMER' | 'PROVIDER'>('CUSTOMER')
  const [localUpdates, setLocalUpdates] = useState<Record<string, any>>({})
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key)

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { data, isLoading } = useContactsFetch(tokenData, {
    all: true,
    company: company.current?.uid,
    role: activeRole,
    page,
    hitsPerPage: pageSize
  })

  useEffect(() => {
    if (data?.contactList) {
      const now = Date.now()
      const validUpdates = { ...localUpdates }
      let hasExpired = false
      for (const uid in validUpdates) {
        if (now - validUpdates[uid]._updatedAt > 15000) {
          delete validUpdates[uid]
          hasExpired = true
        }
      }
      if (hasExpired) setLocalUpdates(validUpdates)

      const mergedList = data.contactList.map((c: any) => {
        if (validUpdates[c.uid]) {
          return { ...c, ...validUpdates[c.uid] }
        }
        return c
      })

      // Add NEW contacts that are in validUpdates but not in data.contactList yet
      Object.keys(validUpdates).forEach(uid => {
        if (!mergedList.find((c: any) => c.uid === uid)) {
          mergedList.unshift(validUpdates[uid])
        }
      })

      updateContact({ list: mergedList })
    }
  }, [data, localUpdates])

  const handleSearch = async (text: string) => {
    const path = text ? '/user/contact/search-bulk-contacts' : '/user/contact/get-contact'
    const contactResponse = await securePost(path, {
      company: company.current?.uid,
      all: !text,
      role: activeRole,
      type: "full",
      search: text,
      module: config.module
    })
    
    if (contactResponse.status === 200) {
      updateContact({ list: contactResponse.contactList })
    }
  }

  const handleSave = async (values: any) => {
    const isEdit = !!values.uid
    const dataRequest: any = {
      company: company.current?.uid,
      module: config.module,
      contact: { ...values, company_id: company.current?.id }
    }

    let url = "/user/contact/create-contact"
    if (isEdit) {
      dataRequest.uidContact = values.uid
      dataRequest.contact.id = values.id
      url = "/user/contact/update-contact"
    } else {
      dataRequest.contact.roles = [{ role_type: activeRole }]
    }

    const res = await securePost(url, dataRequest)
    if (res.status !== 200) {
      throw new Error(res.message || "Error al guardar el contacto")
    }

    const updatedContact = res.contact || values;
    if (updatedContact && !updatedContact.uid && values.uid) {
      updatedContact.uid = values.uid;
    }

    if (updatedContact.uid) {
      setLocalUpdates(prev => ({
        ...prev,
        [updatedContact.uid]: { ...updatedContact, _updatedAt: Date.now() }
      }))
    }

    mutate(key => Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/user/contact/get-contact'), undefined, { revalidate: true })
  }

  const handleEditRequest = async (record: any) => {
    const res = await securePost('/user/contact/get-contact', {
      company: company.current?.uid,
      uidContact: record.uid
    })
    
    if (res.status && res.status !== 200) {
      throw new Error(res.message || "Error al obtener el contacto")
    }

    const contactData = res.contact || res;

    if (contactData && !contactData.uid && record.uid) {
      contactData.uid = record.uid
    }
    if (contactData && !contactData.id && record.id) {
      contactData.id = record.id
    }

    return contactData
  }

  const columns = [
    { title: "Nombre", dataIndex: "name", key: "name", sorter: (a: any, b: any) => a.name.localeCompare(b.name) },
    { title: "ID", dataIndex: "document_number", key: "document_number" },
    { title: "Teléfono", dataIndex: "phone", key: "phone" },
    { title: "Correo", dataIndex: "email", key: "email" },
  ]

  return (
    <UnifiedContactView
      title="Directorio de Contactos"
      roles={[
        { key: 'CUSTOMER', label: 'Clientes', icon: <TeamOutlined style={{ marginRight: 8 }} /> },
        { key: 'PROVIDER', label: 'Proveedores', icon: <UserOutlined style={{ marginRight: 8 }} /> }
      ]}
      activeRole={activeRole}
      onRoleChange={(key) => setActiveRole(key as any)}
      dataSource={contactState?.list || []}
      loading={isLoading}
      onSearch={handleSearch}
      onSave={handleSave}
      onEditRequest={handleEditRequest}
      onSuccess={() => {}}
      validationMode="strict"
      columns={columns}
      pagination={{
        current: page + 1,
        pageSize,
        total: data?.nbHits || 0,
        onChange: (p: number, ps: number) => {
          setPage(p - 1)
          if (ps !== pageSize) {
            setPageSize(ps)
            setPage(0)
          }
        },
        showSizeChanger: false,
        showTotal: (total: number) => `Total ${total} contactos`
      }}
    />
  )
}

export default ViewUserContactList